import { axiosInstance } from "contexts/axiosApiInstance";
import {
  RevokeAccessRequest,
  SharedAccess,
  TableOwnershipTransfer,
} from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import { wasmBody } from "components/common/wasmBody";
import { Dispatch, SetStateAction, useState, useCallback } from "react";
import { RemoveAccessData } from "components/shareSection/entities";
import { useBoundStore } from "components/canvas/stores/useAppStore";
const log = createLogger("TableServices");

/// Why an organization can't receive this table. `org_boundary`,
/// `catalog_direction` and `connection_org` are facts about where the table
/// may go at all, so the picker leaves those orgs out; the rest are real
/// destinations that refuse right now, so they stay visible and disabled.
export type OrgRefusal =
  | "org_boundary"
  | "catalog_direction"
  | "connection_org"
  | "parent_lock"
  | "storage_full"
  | "connection_unresolvable"
  | "connection_org_unavailable";

/// Why a model can't receive it. Only the two things that vary model to model
/// — the org-shaped refusals live on the org.
export type ModelRefusal = "not_manager" | "sealed";

export interface TransferQuota {
  used_bytes: number;
  total_bytes: number;
}

/// One organization the table could move into. `detaches_source` is the whole
/// reason the picker asks for an org first: it decides whether the table stays
/// behind as an import and whether its links survive.
export interface OrgDestination {
  /// Absent for the public catalog, which is outside every org.
  org_id?: string;
  is_catalog: boolean;
  can_receive: boolean;
  reason?: OrgRefusal;
  detaches_source: boolean;
  /// Present only on a `storage_full` row — the figures behind the refusal.
  quota?: TransferQuota;
}

export interface ModelDestination {
  model_id: string;
  /// The org this model bills to, so rows group under the org that owns them.
  /// Absent for a catalog model.
  org_id?: string;
  is_catalog: boolean;
  can_receive: boolean;
  reason?: ModelRefusal;
}

/// A table that loses links when the move detaches the source.
export interface SeveredLinkTable {
  table_id: string;
  table_name: string;
  model_id: string;
  model_name: string;
  link_count: number;
}

export interface TransferDestinations {
  source: {
    model_id: string;
    org_id?: string;
    is_catalog: boolean;
  };
  table_size_bytes: number;
  orgs: OrgDestination[];
  models: ModelDestination[];
  /// Empty unless some destination detaches. The set depends only on the
  /// source org, so it is the same whichever detaching destination is picked.
  /// Only tables the caller can see — each row names one and its model.
  severed_links: SeveredLinkTable[];
  /// Severed links to tables the caller cannot see: the move still removes
  /// them, so they are counted, never named. Absent from an older server.
  hidden_severed_link_count?: number;
  /// Every link the caller can see on the table, severed or not — what tells
  /// "all of them survive" apart from "there are none". Absent when the server
  /// could not read it.
  total_link_count?: number;
}

/// Matches the server cap in `ownership/destinations.rs`.
const DESTINATIONS_BATCH = 100;

/// Where this table can go, and what moving it costs. The endpoint composes
/// the same predicates the door does — org boundary, sealed wall, catalog
/// direction, parent lock, destination quota — so the picker can never offer a
/// destination the transfer then refuses.
///
/// Only `models` is per-batch; everything else describes the table and its
/// source org, so it is read from the first answer.
export const fetchTransferDestinations = async (
  tableId: string,
  modelIds: string[],
): Promise<TransferDestinations> => {
  const batches: string[][] = [];
  for (let i = 0; i < modelIds.length; i += DESTINATIONS_BATCH) {
    batches.push(modelIds.slice(i, i + DESTINATIONS_BATCH));
  }
  if (batches.length === 0) batches.push([]);

  const results = await Promise.all(
    batches.map((batch) =>
      axiosInstance
        .post(`/action/ownership_transfer/table/destinations`, {
          table_id: tableId,
          model_ids: batch,
        })
        .then((r) => r.data as TransferDestinations),
    ),
  );
  return {
    ...results[0],
    models: results.flatMap((r) => r.models ?? []),
  };
};

export const handleTableOwnershipTransfer = async (
  tableId: string,
  fromModel: string,
  toModel: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const request = TableOwnershipTransfer.new_js(tableId, fromModel, toModel);
    await axiosInstance.post(
      `/action/ownership_transfer/table`,
      wasmBody(request),
    );
    return {
      success: true,
      message: "Ownership Transfer Completed!",
    };
  } catch (e) {
    log.error("Ownership transfer failed", e);
    return {
      success: false,
      message: "Failed to transfer table ownership!",
    };
  }
};

export interface FetchSharedResult {
  sharedAccess: SharedAccess;
  continuationToken: string | null;
}

export const fetchTableSharedWithList = async (
  tableId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  limit?: number,
): Promise<FetchSharedResult | null> => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    if (continuationToken) {
      params.append("continuation_token", continuationToken);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    const queryString = params.toString();
    const url = `/action/table/shared_with/${tableId}/users${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    const data = response.data;

    const rawSharedAccess = {
      users: data.users ?? [],
    };
    const newAccess = SharedAccess.from_js(rawSharedAccess);
    const nextToken: string | null = data.continuation_token ?? null;

    if (continuationToken) {
      // Append mode: merge new page into existing list
      setSharedWithList((prev) => {
        if (prev) {
          return prev.extend_js(rawSharedAccess);
        }
        return newAccess;
      });
    } else {
      setSharedWithList((prev) =>
        prev ? prev.replace_users_js(rawSharedAccess) : newAccess,
      );
    }

    setContinuationToken?.(nextToken);

    return { sharedAccess: newAccess, continuationToken: nextToken };
  } catch (error) {
    log.error("Error getting share with list: %o", error);
    return null;
  }
};

/**
 * Shared hook for "detach this table into a model" UX. Dispatches the
 * `CopyTableRequest` WS action via WASM; the target model's actor
 * orchestrates data_service + MetaSchema + model-state atomically. The
 * client receives ACK Succeeded when all three committed and a
 * `TableDetachSuccess` / `TableDetachFailure` notification fires.
 *
 * The detach can be retried freely — the server no longer enforces
 * one-detach-per-user. Each click creates a new independent owned copy.
 */
export function useCopyTable(sourceTableId: string | undefined) {
  const clientApp = useBoundStore((state) => state.clientApp);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(false);

  const copy = useCallback(
    async (targetModelId: string, sourceVersion?: number) => {
      if (!sourceTableId || copying || !clientApp) return;
      setCopying(true);
      setError(false);
      try {
        clientApp.handle_detach_table_ui(
          sourceTableId,
          targetModelId,
          sourceVersion !== undefined ? BigInt(sourceVersion) : null,
        );
      } catch (e) {
        log.error("Failed to dispatch detach action: %o", e);
        setError(true);
        setCopying(false);
        throw e;
      }
      // The WS ACK comes back asynchronously; the notification system
      // surfaces success or failure. We reset `copying` after a short
      // grace window so the button doesn't stay disabled indefinitely
      // if something silently drops the ACK.
      setTimeout(() => setCopying(false), 500);
    },
    [sourceTableId, copying, clientApp],
  );

  const status: "idle" | "copying" | "error" = copying
    ? "copying"
    : error
      ? "error"
      : "idle";

  // `canCopy` is always true client-side now — the server enforces
  // `UserCanImportTable(source)` when the action is dispatched and
  // returns a clean ACK Failed if the user lacks permission.
  return { copying, error, status, copy, canCopy: true };
}

export const handleTableRevokeAccess = async (
  tableId: string,
  data: RemoveAccessData | null,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  tableName: string,
) => {
  if (!data?.clientId) return;

  const request = RevokeAccessRequest.new_js(
    JSON.stringify({ users: [data.clientId] }),
    tableId,
    data.oldRole,
    tableName,
  );
  const body = wasmBody(request);

  try {
    const response = await axiosInstance.post(
      `/action/revoke_access/table`,
      body,
    );
    log.info("Access revoked successfully:", response.data);
    setSharedWithList((prevList) =>
      prevList?.remove_user_by_email(data.email ?? ""),
    );
  } catch (error) {
    log.error("Failed to revoke access:", error);
    throw error;
  }
};
